import React, { useState, useEffect, useCallback } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import * as LinkedInAPI from '../api/LinkedIn';
import { ImageStyle, LinkedInPost } from '../api/LinkedIn';

const IMAGE_STYLES: { value: ImageStyle; label: string; description: string }[] = [
  { value: 'photorealistic', label: 'Photorealistic', description: 'Corporate lifestyle photo — profession-specific scene, office/field setting' },
  { value: 'infographic', label: 'Infographic', description: 'Data visualization — stats, states, credits in navy/teal cards & charts' },
  { value: 'simple', label: 'Bold Typographic', description: 'Minimal navy graphic — large white text, no photos or people' },
];

const LinkedInAdmin = () => {
  usePageTitle('LinkedIn Content Generator');

  const [topic, setTopic] = useState('');
  const [context, setContext] = useState('');
  const [imageStyle, setImageStyle] = useState<ImageStyle>('photorealistic');

  const [generating, setGenerating] = useState(false);
  const [regeneratingImage, setRegeneratingImage] = useState(false);
  const [error, setError] = useState('');

  const [postText, setPostText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [postId, setPostId] = useState('');
  const [copied, setCopied] = useState(false);

  const [history, setHistory] = useState<LinkedInPost[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [marking, setMarking] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const posts = await LinkedInAPI.listLinkedInPosts();
      setHistory(posts);
    } catch {
      // non-fatal — history panel just stays empty
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleGenerate = async () => {
    if (!topic.trim()) {
      setError('Please enter a topic.');
      return;
    }
    setError('');
    setGenerating(true);
    setPostText('');
    setImageUrl('');
    setPostId('');
    try {
      const result = await LinkedInAPI.generateLinkedInContent({ topic, context, imageStyle });
      setPostText(result.postText);
      setImageUrl(result.imageUrl);
      setPostId(result.postId);
      setImageStyle(result.imageStyle);
      loadHistory();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateImage = async () => {
    if (!postText.trim()) return;
    setError('');
    setRegeneratingImage(true);
    try {
      const result = await LinkedInAPI.regenerateLinkedInImage({ postText, imageStyle, postId: postId || undefined });
      setImageUrl(result.imageUrl);
      setImageStyle(result.imageStyle);
      if (postId) loadHistory();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Image regeneration failed');
    } finally {
      setRegeneratingImage(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(postText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadImage = (url: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `linkedin-image-${Date.now()}.png`;
    a.target = '_blank';
    a.click();
  };

  const handleMarkPosted = async (id: string) => {
    setMarking(id);
    try {
      await LinkedInAPI.markLinkedInPostPosted(id);
      loadHistory();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to mark as posted');
    } finally {
      setMarking(null);
    }
  };

  return (
    <div className="container py-4" style={{ maxWidth: 860 }}>
      <h2 className="mb-1">LinkedIn Content Generator</h2>
      <p className="text-muted mb-4">Generate a professional post + matching image for LinkedIn using AI.</p>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Input Form */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label fw-semibold">Topic <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. CEC Track now supports CPA continuing education tracking"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={generating}
            />
            <div className="form-text">What should the post be about?</div>
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold">Additional Context</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Any specific points to highlight, tone preferences, or stats to include..."
              value={context}
              onChange={(e) => setContext(e.target.value)}
              disabled={generating}
            />
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold">Image Style</label>
            <div className="row g-2">
              {IMAGE_STYLES.map((s) => (
                <div className="col-6 col-md-4" key={s.value}>
                  <div
                    className={`card h-100 cursor-pointer border-2 ${imageStyle === s.value ? 'border-primary bg-primary bg-opacity-10' : 'border-light'}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setImageStyle(s.value)}
                  >
                    <div className="card-body p-2 text-center">
                      <div className="fw-semibold small">{s.label}</div>
                      <div className="text-muted" style={{ fontSize: '0.7rem' }}>{s.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={generating || !topic.trim()}
          >
            {generating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Generating… (may take ~20s)
              </>
            ) : (
              'Generate Post + Image'
            )}
          </button>
        </div>
      </div>

      {/* Results */}
      {(postText || imageUrl) && (
        <div className="row g-3 mb-4">
          {/* Post Text */}
          {postText && (
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">Generated Post</span>
                  <button className="btn btn-sm btn-outline-secondary" onClick={handleCopy}>
                    {copied ? '✓ Copied!' : 'Copy Text'}
                  </button>
                </div>
                <div className="card-body">
                  <textarea
                    className="form-control font-monospace"
                    rows={10}
                    value={postText}
                    onChange={(e) => setPostText(e.target.value)}
                    style={{ fontSize: '0.9rem', resize: 'vertical' }}
                  />
                  <div className="form-text mt-1">You can edit the post text above before copying.</div>
                </div>
              </div>
            </div>
          )}

          {/* Image */}
          {imageUrl && (
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">Generated Image</span>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleRegenerateImage}
                      disabled={regeneratingImage}
                    >
                      {regeneratingImage ? (
                        <><span className="spinner-border spinner-border-sm me-1" />Regenerating…</>
                      ) : (
                        'New Image'
                      )}
                    </button>
                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleDownloadImage(imageUrl)}>
                      Download
                    </button>
                  </div>
                </div>
                <div className="card-body p-2 text-center">
                  <img
                    src={imageUrl}
                    alt="Generated LinkedIn post image"
                    className="img-fluid rounded"
                    style={{ maxHeight: 400, objectFit: 'contain' }}
                  />
                  <div className="form-text mt-2">
                    Saved permanently to S3. Post to LinkedIn manually, then mark it posted below.
                  </div>
                </div>
              </div>
            </div>
          )}

          {postId && (
            <div className="col-12">
              <button
                className="btn btn-sm btn-outline-success"
                disabled={marking === postId || history.find(h => h._id === postId)?.status === 'posted'}
                onClick={() => handleMarkPosted(postId)}
              >
                {history.find(h => h._id === postId)?.status === 'posted' ? '✓ Marked as Posted' : 'Mark as Posted'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Post History */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span className="fw-semibold">Post History</span>
          <button className="btn btn-sm btn-outline-secondary" onClick={loadHistory} disabled={historyLoading}>
            {historyLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        <div className="card-body">
          {history.length === 0 && !historyLoading && (
            <p className="text-muted mb-0">No posts generated yet.</p>
          )}
          <div className="row g-3">
            {history.map((post) => (
              <div className="col-12 col-md-6 col-lg-4" key={post._id}>
                <div className="card h-100">
                  <img
                    src={post.imageUrl}
                    alt={post.topic}
                    className="card-img-top"
                    style={{ height: 140, objectFit: 'cover' }}
                  />
                  <div className="card-body p-2">
                    <div className="fw-semibold small text-truncate" title={post.topic}>{post.topic}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {new Date(post.createdAt).toLocaleDateString()} · {post.imageStyle}
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <span className={`badge ${post.status === 'posted' ? 'bg-success' : 'bg-secondary'}`}>
                        {post.status === 'posted' ? '✓ Posted' : 'Generated'}
                      </span>
                      {post.status === 'generated' && (
                        <button
                          className="btn btn-sm btn-outline-success"
                          disabled={marking === post._id}
                          onClick={() => handleMarkPosted(post._id)}
                        >
                          Mark Posted
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkedInAdmin;
